/**
 * Czech copy of the /docs page — the chrome of /cz/docs (2026-09-28, Teo: "Czech chrome
 * on cz/docs"). Same shape as the Slovenian set (`DocsCopy`, docsCopySl.ts): a key
 * missing here fails the build.
 *
 * DRAFT — machine-authored, pending a native speaker's review, the same status as the
 * #597 Czech map strings. Czech counts: 1 vrstva, 2–4 vrstvy, 5+ vrstev (no dual, and
 * 21–24 take the genitive plural, unlike Slovenian).
 */

import type { WikiCountry } from '../../shared/types/layerWiki.js';
import { BRAND } from '../../shared/utils/brand.js';
import type { DocsCopy } from './docsCopySl.js';

/** Czech count word: 1 / 2–4 / 0 and 5+. */
function plural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  if (n >= 2 && n <= 4) return few;
  return many;
}

function vrstev(n: number): string {
  return `${n} ${plural(n, 'vrstva', 'vrstvy', 'vrstev')}`;
}

function vydavatelu(n: number): string {
  return `${n} ${plural(n, 'vydavatel', 'vydavatelé', 'vydavatelů')}`;
}

function slozek(n: number): string {
  return `${n} ${plural(n, 'složka', 'složky', 'složek')}`;
}

/** Locative after »v«: v 1 složce, v 2+ složkách. */
function slozkach(n: number): string {
  return `${n} ${n === 1 ? 'složce' : 'složkách'}`;
}

function temat(n: number): string {
  return `${n} ${plural(n, 'téma', 'témata', 'témat')}`;
}

export const docsCopyCs: DocsCopy = {
  origin: {
    imps: 'záznam metadat INSPIRE',
    opsi: 'OPSI (podatki.gov.si)',
    publisher_page: 'webová stránka zdroje',
    cuzk_metadata: 'metadata ČÚZK',
    osm_wiki: 'OSM wiki',
    sxlayer_desc: 'popis vrstvy v KingCAD',
    king: BRAND.name,
  } as Record<string, string>,

  count: (n: number): string => vrstev(n),
  publishers: (n: number): string => vydavatelu(n),
  /** Instrumental after »před«: před 1 dnem, před 5 dny; před 1 měsícem, před 3 měsíci; před 1 rokem, před 2 lety. */
  age: {
    today: 'dnes',
    days: (n: number): string => `před ${n} ${n === 1 ? 'dnem' : 'dny'}`,
    months: (n: number): string => `před ${n} ${n === 1 ? 'měsícem' : 'měsíci'}`,
    years: (n: number): string => `před ${n} ${n === 1 ? 'rokem' : 'lety'}`,
  },
  updated: 'Aktualizováno',
  updatedAgo: (age: string): string => `Aktualizováno ${age}`,

  searchPlaceholder: 'Hledat vrstvu nebo zdroj …',
  searchLabel: 'Hledat vrstvu nebo zdroj',

  sections: { web: `${BRAND.name} Web`, cad: `${BRAND.name} CAD` },
  tabs: { pregled: 'Přehled', katalog: `${BRAND.name} Web`, cad: `${BRAND.name} CAD`, viri: 'Zdroje a licence' },
  tabsLabel: 'Části dokumentace',

  header: {
    suffix: '/ Dokumentace',
    homeLabel: 'Dokumentace vrstev — domů',
    testBadge: 'Testovací vydání',
    lang: 'CS',
    siteLink: `${BRAND.siteHost} ↗`,
    countries: 'Data podle zemí',
    countryNames: { si: 'Slovinsko', cz: 'Česko' } as Record<WikiCountry, string>,
    appLinks: 'Zpět do aplikace',
    mapLink: 'Mapa',
    homeLink: 'Úvodní stránka',
  },

  hero: {
    eyebrow: 'Zdroje, licence a stav dat',
    title: `${BRAND.name}. Vrstva po vrstvě.`,
    lead: `Ke každé vrstvě, kterou ${BRAND.name} spravuje: co je slovy vydavatele, kdo ji publikuje a pod jakou licencí, jak často ji obnovujeme a odkdy webová mapa a KingCAD zobrazují aktuální data.`,
    cta: 'Prohlédněte si katalog vrstev →',
    viriLink: 'Zdroje a licence →',
  },
  stats: { layers: `vrstev v ${BRAND.name} Web`, cadLayers: `vrstev v ${BRAND.name} CAD`, sources: 'zdrojů', themes: 'témat' },
  next: {
    heading: 'Jak chcete pokračovat?',
    katalog: {
      eyebrow: 'Prohlédněte',
      title: `${BRAND.name} Web`,
      text: (nLayers: number): string => `Všech ${vrstev(nLayers)} podle témat — popis, data, stav a kde vrstva žije. →`,
    },
    cad: {
      eyebrow: 'Procházejte',
      title: `${BRAND.name} CAD`,
      text: (nLayers: number, nFolders: number): string =>
        `Strom vrstev, jak ho zobrazuje KingCAD — ${vrstev(nLayers)} v ${slozkach(nFolders)}. →`,
    },
    viri: {
      eyebrow: 'Pochopte',
      title: 'Zdroje a licence',
      text: (nSources: number): string => `${vydavatelu(nSources)}, jejich licence a povinné uvedení zdroje. →`,
    },
  },

  viri: {
    eyebrow: 'Dokumentace vrstev',
    title: 'Zdroje a licence',
    intro: (nSources: number, nOwnLicence: number): string =>
      `${vydavatelu(nSources)}; uvedení zdroje je povinný dovětek, který mapa zobrazí při použití vrstvy. ${vrstev(nOwnLicence)} ${nOwnLicence === 1 ? 'nese' : 'nesou'} vlastní licenci, která má přednost před licencí zdroje.`,
    licencePerEntry: 'Licence podle jednotlivé vrstvy',
    attribution: 'Uvedení zdroje',
    sourceSite: 'Webová stránka zdroje ↗',
    tocLabel: 'Vydavatelé',
  },

  web: {
    title: `${BRAND.name} Web`,
    crumb: `${BRAND.name} Web`,
    intro: (nThemes: number, nLayers: number): string => `${temat(nThemes)} · ${vrstev(nLayers)}`,
    navLabel: `Vrstvy ${BRAND.name} Web`,
    navToggle: (n: number): string => `Vrstvy ${BRAND.name} Web · ${n}`,
    page: {
      onThisPage: 'Vrstvy',
      notFound: 'K tomuto tématu není v dokumentaci záznam.',
      notFoundLink: `Na přehled ${BRAND.name} Web →`,
    },
  },

  cad: {
    title: `${BRAND.name} CAD`,
    crumb: `${BRAND.name} CAD`,
    intro: (nPages: number, nLayers: number): string => `${slozek(nPages)} · ${vrstev(nLayers)}`,
    navLabel: `Vrstvy ${BRAND.name} CAD`,
    navToggle: (n: number): string => `Vrstvy ${BRAND.name} CAD · ${n}`,
    folderToggle: (name: string): string => `Složka ${name}`,
    rastriFolder: 'Rastry a LiDAR',
    layersIn: (n: number): string => vrstev(n),
    noEntry: 'Vrstva ještě nemá záznam v dokumentaci.',
    missingFolder: 'Mapa této složky není registrována.',
    unavailable: 'Strom vrstev KingCAD není momentálně k dispozici.',
    folder: {
      onThisPage: 'Vrstvy',
      subfolders: 'Podsložky',
      notFound: 'K této složce není v dokumentaci záznam.',
      notFoundLink: `Na přehled ${BRAND.name} CAD →`,
    },
  },
  noDescription: 'Popis ještě nebyl napsán.',

  katalog: {
    crumbRoot: 'Dokumentace',
    altTitle: 'slovinsky',
    altHeading: 'Slovinsky',
    prev: '← Předchozí',
    next: 'Další →',
    onThisPage: 'Na této stránce',
    permalink: 'Trvalý odkaz:',
    rail: { opis: 'Popis', podatki: 'Údaje', povezave: 'Odkazy', povezani: 'Související vrstvy', sestavni: 'Součásti', zgodovina: 'Historie úprav' },
    notFoundLink: `Na přehled ${BRAND.name} Web →`,
    partOf: (parentTitle: string): string => `část: ${parentTitle}`,
  },

  facts: {
    source: 'Zdroj',
    vintage: 'Datum zdroje',
    acquisition: 'Rok snímání',
    web: `Datum ${BRAND.name} web`,
    desktop: `Datum ${BRAND.name} CAD`,
    origin: 'Text',
    edited: (date: string): string => `upraveno ${date}`,
    unknownDate: '—',
    unknownDateTitle: 'Datum není zaznamenáno v evidenci importů',
  },

  edit: {
    button: 'Upravit popis',
    title: 'Úprava popisu',
    sl: 'Popis (slovinsky)',
    cs: 'Popis (česky) — nepovinné',
    note: 'Poznámka ke změně (nepovinné)',
    hint: 'Prázdný řádek začíná nový odstavec. Bez formátování a odkazů.',
    save: 'Uložit',
    saving: 'Ukládám …',
    cancel: 'Zrušit',
    history: 'Historie úprav',
    noHistory: 'Text ještě nebyl ručně upraven.',
    historyFailed: 'Historii úprav se nepodařilo načíst.',
    previous: 'Předchozí text',
    restore: 'Obnovit',
    restoreConfirm: (when: string): string => `Obnovit text, jaký byl před změnou ze dne ${when}?`,
    actions: { text_update: 'úprava', text_restore: 'obnovení' } as Record<'text_update' | 'text_restore', string>,
    errors: {
      emptySl: 'Slovinský popis nesmí být prázdný.',
      noChange: 'Žádné změny k uložení.',
      forbidden: 'Pro úpravy se přihlaste v prohlížeči jako správce.',
      notFound: 'Tento záznam už neexistuje.',
      generic: 'Uložení se nezdařilo. Zkuste to znovu.',
    },
  },
  links: {
    publisher: 'Webová stránka zdroje',
    metadata: 'Metadata (INSPIRE / OPSI)',
    heading: 'Odkazy',
  },
  componentsHeading: 'Součásti',
  related: {
    heading: 'Související vrstvy',
    parent: { part: 'Část vrstvy', computed: 'Vypočteno z vrstvy', preset: 'Zvláštní pohled na vrstvu', cad: 'Datová vrstva' },
    children: { part: 'Podřízené vrstvy', computed: 'Odvozené vrstvy', preset: 'Zvláštní pohledy', cad: `Vrstvy ${BRAND.name} CAD` },
  },
  /** "3 304 listů" — Czech groups thousands with a space. */
  sheets: (n: number): string => {
    const word = plural(n, 'list', 'listy', 'listů');
    return `${String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ${word}`;
  },
  parked: 'pozastaveno',

  tilesBehind: 'starší než import',
  tilesLive: 'živě',
  tilesDated: 'Datum dlaždic',
  surface: { web: 'Pouze web' },

  titles: {
    base: `Dokumentace vrstev – ${BRAND.name}`,
    viri: `Zdroje a licence – Dokumentace vrstev – ${BRAND.name}`,
    web: `${BRAND.name} Web – Dokumentace vrstev – ${BRAND.name}`,
    webPage: (theme: string): string => `${theme} – ${BRAND.name} Web – Dokumentace vrstev – ${BRAND.name}`,
    cad: `${BRAND.name} CAD – Dokumentace vrstev – ${BRAND.name}`,
    cadFolder: (name: string): string => `${name} – ${BRAND.name} CAD – Dokumentace vrstev – ${BRAND.name}`,
    entry: (title: string): string => `${title} – Dokumentace vrstev – ${BRAND.name}`,
    notFound: `Bez záznamu – Dokumentace vrstev – ${BRAND.name}`,
  },

  error: 'Dokumentace vrstev není momentálně k dispozici.',
  retry: 'Zkusit znovu',
  emptySearch: (q: string): string => `Žádné vrstvy pro „${q}“.`,
  clearSearch: 'Vymazat hledání',
  noEntries: 'Dokumentace ještě nemá záznamy.',
  notFound: 'K této vrstvě ještě není v dokumentaci záznam.',
  loading: 'Načítám …',
};
